import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  CohereTable,
  colorsLight,
  getCaseRedirectUrl,
  H3,
  PaginateProps,
  Tooltip,
  useIsOverflow,
} from "@coherehealth/common";
import { ServiceCaseUserHold, useSearchServiceCaseUserHoldsInQueue } from "@coherehealth/qm-api";
import { makeStyles, useTheme, createStyles } from "@material-ui/core/styles";
import { userHoldsTableColumnsConfig } from "util/TableUtil";
import { ClassNameMap, Theme, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { UserContext } from "UserContext";

interface Props {
  activeQueueId?: string;
}

export default function DashboardUserHolds({ activeQueueId }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const theme = useTheme();
  const styles = useStyles(theme);

  const [userHoldsQueryParams, setUserHoldsQueryParams] = useState<PaginateProps>({ offset: 0 });
  const { activeQueue } = useContext(UserContext);

  const {
    loading,
    error,
    data: holds,
    refetch: fetchUserServiceCases,
  } = useSearchServiceCaseUserHoldsInQueue({ lazy: true, queueId: activeQueueId || "" });

  useEffect(() => {
    if (activeQueueId) {
      fetchUserServiceCases({ queryParams: { offset: userHoldsQueryParams.offset || 0 } });
    }
  }, [activeQueueId, fetchUserServiceCases, userHoldsQueryParams.offset]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar("Failed to get user holds", {
        variant: "error",
        preventDuplicate: true,
      });
    }
  }, [error, enqueueSnackbar]);

  const userHoldsTableColumns = useMemo(() => {
    const columns = userHoldsTableColumnsConfig();

    const reasonColumnIndex = columns.findIndex((column) => column.name === "otherReason");
    if (columns[reasonColumnIndex]) {
      columns[reasonColumnIndex].value = (hold: ServiceCaseUserHold) => HoldReasonWrapper(hold, styles);
    }
    /*
    Uncoment when the 'dismiss' action is enabled

    columns.push({
      name: "",
      label: "",
      width: "120px",
      themedPaddingRight: 0,
      value: (hold: ServiceCaseUserHold) => <TertiaryButton className={styles.dismissBtn}>Dismiss</TertiaryButton>,
    });
    */

    return columns;
  }, [styles]);

  return (
    <div className={holds?.totalSize ? styles.root : styles.emptyRoot}>
      <div>
        <H3 style={{ marginLeft: theme.spacing(2), marginBottom: -theme.spacing(1), fontWeight: "bold" }}>Holds</H3>
      </div>
      <CohereTable<ServiceCaseUserHold>
        loading={loading}
        tableColumns={userHoldsTableColumns}
        data={holds?.content || []}
        totalCount={holds?.totalSize || 0}
        rowsHeight="calc( 100vh - 413px )"
        wideRows
        withDivider
        paginateViaParams
        paginateParams={userHoldsQueryParams}
        setPaginateParams={setUserHoldsQueryParams}
        displayMax={10}
        hideFinalDivider
        hideRowPerPageDropDown
        emptyListCaption="There are no cases currently on hold"
        containerFontColor={colorsLight.font.light}
        onRowClick={(holdCase: ServiceCaseUserHold) => {
          const serviceCase = holdCase?.serviceCase;
          const caseUrl = getCaseRedirectUrl({
            caseId: serviceCase?.id,
            caseType: serviceCase?.caseType,
            serviceRequestId: serviceCase?.serviceRequest?.serviceRequestId,
            faxId: serviceCase?.faxDetail?.attachmentId,
            isPostDenialP2P: serviceCase?.isPostDenialP2P,
            fromMyWork: true,
            queueId: activeQueueId,
            routeConfigByCaseType: activeQueue?.routeConfigByCaseType,
          });
          caseUrl && window.location.assign(caseUrl);
        }}
      />
    </div>
  );
}

const HoldReasonWrapper = (
  hold: ServiceCaseUserHold,
  styleClasses: ClassNameMap<"tableColumn" | "styledBodyContainer">
) => {
  const textElementRef = useRef<HTMLInputElement | null>(null);
  const { hoverStatus } = useIsOverflow(textElementRef);
  const reason = hold.otherReason ? `Other - ${hold.otherReason}` : hold.reason;

  return (
    <div className={styleClasses.tableColumn}>
      <Tooltip title={reason || ""} placement="top" disableHoverListener={!hoverStatus}>
        <Typography className={styleClasses.styledBodyContainer} ref={textElementRef}>
          {reason}
        </Typography>
      </Tooltip>
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      minHeight: "150px",
      width: "100%",
    },
    emptyRoot: {
      minHeight: "150px",
      marginBottom: theme.spacing(7),
    },
    tableColumn: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
    },
    styledBodyContainer: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    dismissBtn: {
      padding: theme.spacing(1.5),
      wordBreak: "normal",
    },
  })
);
